import React from "react";
import { frDate, capi, CAPI } from "./clientApi";

const DOC_KIND_LABEL = { brief: "Brief", devis: "Devis", facture: "Facture", contrat: "Contrat", autre: "Document" };

async function openDoc(id, download) {
  try {
    const r = await capi(`/documents/${id}/link`, { method: "POST" });
    if (!r.ok) return;
    const { token } = await r.json();
    window.open(`${CAPI}/files/${token}${download ? "?download=1" : ""}`, "_blank", "noopener");
  } catch (_) { /* silencieux */ }
}

export function DocRow({ doc, onOpen }) {
  const open = onOpen || openDoc;
  return (
    <div className="cp-doc" data-testid={`cp-doc-${doc.id}`}>
      <span className="cp-doc-icon" aria-hidden="true">
        <svg width="42" height="48" viewBox="0 0 42 48" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 2h22l8 8v36H6z" fill="#fff" />
          <path d="M28 2v8h8" />
          <text x="21" y="34" textAnchor="middle" fontSize="9" fontWeight="700" stroke="none" fill="currentColor" fontFamily="Archivo">PDF</text>
        </svg>
      </span>
      <div className="cp-doc-body">
        <div className="cp-doc-title">{doc.title || doc.original_filename}</div>
        <div className="cp-doc-meta">{[DOC_KIND_LABEL[doc.kind] || doc.kind, doc.meta].filter(Boolean).join(" · ") || "—"}</div>
      </div>
      <div className="cp-doc-actions">
        <button className="cp-doc-btn" onClick={() => open(doc.id, false)} data-testid={`cp-doc-view-${doc.id}`}>Voir</button>
        <button className="cp-doc-dl" aria-label="Télécharger" title="Télécharger" onClick={() => open(doc.id, true)} data-testid={`cp-doc-dl-${doc.id}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16" /></svg>
        </button>
      </div>
    </div>
  );
}

const KIND_LABEL = { storyboard: "Storyboard", video: "Vidéo · BAT" };
const CHAN_COLORS = ["var(--navy)", "#2D5BFF", "#A9BDFF", "#6B7180", "#C6D3E3"];
const eur = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0)) + " €";

function pillClass(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("valider") || l.includes("cours") || l.includes("traiter")) return "now";
  if (l.includes("revoir")) return "revoir";
  if (l.includes("fait") || l.includes("approuv")) return "done";
  return "todo";
}

function nextAction(data) {
  const dv = data.deliverables.find((d) => (d.status_label || "").toLowerCase().includes("valider"));
  if (dv) {
    const name = KIND_LABEL[dv.kind] || dv.kind;
    return { phrase: `Le ${name.toLowerCase()} ${dv.version_label || ""} attend votre validation`.replace("  ", " "),
             detail: [dv.version_label, dv.updated_at ? "Mis à jour le " + frDate(dv.updated_at) : ""].filter(Boolean).join(" · "),
             btn: `Ouvrir le ${name.toLowerCase()}` };
  }
  const now = data.milestones.find((m) => m.state === "now");
  if (now) return { phrase: `Étape en cours : ${now.title}`, detail: "", btn: null };
  return { phrase: "Aucune action requise pour le moment.", detail: "", btn: null };
}

function Phase({ label, cls, items }) {
  return (
    <div>
      <p className={`lbl cp-phase-title ${cls}`}>{label}</p>
      <div className="cp-frieze">
        {items.map((m) => (
          <div className={`cp-ms ${m.state} ${m.state === "done" ? "after-done" : ""}`} key={m.id} data-testid={`cp-ms-${m.id}`}>
            <span className="connector" />
            <span className={`cp-dot ${m.state}`} />
            <p className="cp-ms-title">{m.title}</p>
            <p className="cp-ms-date">{m.date ? frDate(m.date) : "Date à préciser"}</p>
            <span className={`cp-pill ${m.state}`}>{m.state === "done" ? "Fait" : m.state === "now" ? "En cours" : "À venir"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CampaignDashboardView({ data, onOpenDocument }) {
  if (!data) return null;
  const c = data.campaign;
  const ms = data.milestones || [];
  const creation = ms.filter((m) => m.phase === "creation");
  const production = ms.filter((m) => m.phase === "production");
  const done = ms.filter((m) => m.state === "done").length;
  const total = ms.length || 7;
  const na = nextAction(data);
  const channels = data.channels || [];
  const totalBudget = channels.reduce((s, ch) => s + (ch.budget_eur || 0), 0);

  return (
    <div data-testid="cp-campaign-dashboard">
      <p className="cp-crumb" data-testid="cp-crumb">Mes campagnes / <b>{c.name}</b></p>

      <div className="cp-titlerow">
        <h1 className="cp-h1" data-testid="cp-ch-name">{c.name}</h1>
        <span className="cp-status-big" data-testid="cp-ch-status">{c.status_label || "—"}</span>
      </div>
      <div className="cp-hr" />

      <div className="cp-top">
        <div className="cp-meta">
          <div><span className="lbl">Annonceur</span><div className="v">{c.advertiser_label || "—"}</div></div>
          <div><span className="lbl">Période</span><div className="v">{frDate(c.start_date)} – {frDate(c.end_date)}</div></div>
          <div><span className="lbl">Responsable SLIZ</span><div className="v">{c.owner_name || "—"}</div></div>
          <div><span className="lbl">Formats</span><div className="v">{c.formats || "—"}</div></div>
        </div>
        <div className="cp-next" data-testid="cp-next-action">
          <span className="lbl">Votre prochaine action</span>
          <p className="phrase">{na.phrase}</p>
          {na.detail && <p className="detail">{na.detail}</p>}
          {na.btn && <button className="cp-btn cp-btn-onnavy btn-full" data-testid="cp-next-btn">{na.btn}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg></button>}
        </div>
      </div>

      <div className="cp-panel" data-testid="cp-planning" style={{ marginBottom: 24 }}>
        <div className="cp-panel-head">
          <h2 className="cp-panel-title">Planning</h2>
          <div className="cp-gauge-wrap"><b>{done}</b>&nbsp;étapes sur {total} terminées<span className="cp-gauge"><i style={{ width: `${(done / total) * 100}%` }} /></span></div>
        </div>
        <div className="cp-phases">
          <div data-testid="cp-phase-creation"><Phase label="01 — Création" cls="c1" items={creation} /></div>
          <div className="cp-phase-sep" />
          <div data-testid="cp-phase-production"><Phase label="02 — Production & diffusion" cls="" items={production} /></div>
        </div>
      </div>

      <div className="cp-cards">
        {/* Livrables */}
        <div className="cp-panel" data-testid="cp-livrables">
          <div className="cp-panel-head"><h2 className="cp-panel-title">Livrables</h2><span className="lbl">{data.deliverables.length} éléments</span></div>
          {data.deliverables.map((d) => {
            const todo = pillClass(d.status_label) === "todo";
            return (
              <div className={`cp-deliv ${todo ? "todo" : ""}`} key={d.id} data-testid={`cp-deliv-${d.kind}`}>
                <span className="cp-deliv-thumb">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="6 4 20 12 6 20 6 4"/></svg>
                </span>
                <div className="cp-deliv-body">
                  <div className="cp-deliv-title">{KIND_LABEL[d.kind] || d.kind}<span className={`cp-pill ${pillClass(d.status_label)}`}>{d.status_label || "—"}</span></div>
                  <div className="cp-deliv-meta">{todo ? `Livraison prévue${d.version_label ? " · " + d.version_label : ""}` : [d.version_label, d.updated_at ? frDate(d.updated_at) : ""].filter(Boolean).join(" · ") || "—"}</div>
                </div>
                {!todo && <span className="cp-chevron"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6"/></svg></span>}
              </div>
            );
          })}
        </div>

        {/* Diffusion */}
        <div className="cp-panel" data-testid="cp-diffusion">
          <div className="cp-panel-head"><h2 className="cp-panel-title">Diffusion</h2>{c.diffusion_start && <span className="cp-pill todo">Démarre le {frDate(c.diffusion_start)}</span>}</div>
          {channels.length === 0 ? (
            <p className="cp-empty" data-testid="cp-diffusion-empty">La diffusion n'a pas encore démarré.</p>
          ) : (
            <>
              <span className="lbl">Budget média prévu</span>
              <div className="cp-budget" data-testid="cp-budget">{eur(totalBudget)}</div>
              <div className="cp-bar">
                {channels.map((ch, i) => <i key={ch.id} style={{ flex: (ch.budget_eur || 1), background: CHAN_COLORS[i] || CHAN_COLORS[CHAN_COLORS.length - 1] }} />)}
              </div>
              {channels.map((ch, i) => (
                <div className="cp-chan" key={ch.id} data-testid={`cp-channel-${ch.id}`}>
                  <span className="sq" style={{ background: CHAN_COLORS[i] || "#6B7180" }} />
                  <span className="nm">{ch.channel_name}</span>
                  <span className="est">{ch.estimate_label || ""}</span>
                  <span className="bud">{eur(ch.budget_eur)}</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Documents */}
        <div className="cp-panel" data-testid="cp-documents">
          <div className="cp-panel-head"><h2 className="cp-panel-title">Documents</h2></div>
          {(!data.documents || data.documents.length === 0) ? (
            <p className="cp-empty" data-testid="cp-documents-empty">Aucun document pour le moment.</p>
          ) : (
            <div data-testid="cp-documents-list">
              {data.documents.map((doc) => <DocRow doc={doc} key={doc.id} onOpen={onOpenDocument} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
